import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { Query } from 'react-apollo';

import { graphql, NextInstructionsRenderProps, utils, DailyInstruction, themes } from 'kb-shared';
import Checklist from './Checklist';
import Acknowledgment from './Acknowledgment';
import { IntercourseReminder } from './IntercourseReminder';
import { TabNavigator } from 'kb-web-shared';
const { isToday } = utils;

enum Day {
  today = 0,
  tomorrow = 1,
  nextDay = 2
}

type TabData = {
  key: string;
  title: string;
  data: DailyInstruction;
};

const MedicationsWidget = () => {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Query query={graphql.query.NEXT_INSTRUCTIONS}>
      {({ data, error, loading }: NextInstructionsRenderProps) => {
        if (!data || !data.nextInstructions || loading || error) {
          return null;
        }

        const todayMoment = moment(Date.now());
        const tomorrowMoment = moment(todayMoment).add(1, 'days');
        const nextDayMoment = moment(todayMoment).add(2, 'days');
        const instructions = data.nextInstructions
          // @ts-ignore
          .sort((a, b) =>
            a.date !== undefined &&
            b.date !== undefined &&
            moment(a.date.slice(0, 10)).isAfter(moment(b.date.slice(0, 10)))
              ? 1
              : -1
          )
          .reduce(
            // @ts-ignore
            (acc, ins) => {
              // @ts-ignore
              const date = ins.date.slice(0, 10);

              // @ts-ignore
              if (isToday(date)) return { ...acc, [Day.today]: ins };

              // @ts-ignore
              if (moment(date).isSame(tomorrowMoment, 'day'))
                // @ts-ignore
                return { ...acc, [Day.tomorrow]: ins };

              // @ts-ignore
              if (moment(date).isSame(nextDayMoment, 'day'))
                // @ts-ignore
                return { ...acc, [Day.nextDay]: ins };

              return acc;
            },
            {
              [Day.today]: null,
              [Day.tomorrow]: null,
              [Day.nextDay]: null
            }
          );

        const items: Array<TabData> = [];
        const today = instructions[Day.today];
        const tomorrow = instructions[Day.tomorrow];
        const nextDay = instructions[Day.nextDay];

        if (today)
          items.push({
            key: 'today',
            title: `Today\n${todayMoment.format('ddd MMM DD')}`,
            // @ts-ignore
            data: today
          });

        if (tomorrow)
          items.push({
            key: 'tomorrow',
            title: `Tomorrow\n${tomorrowMoment.format('ddd MMM DD')}`,
            // @ts-ignore
            data: tomorrow
          });

        if (nextDay)
          items.push({
            key: 'next-day',
            title: `Next day\n${nextDayMoment.format('ddd MMM DD')}`,
            // @ts-ignore
            data: nextDay
          });

        if (items.length === 0) return null;

        const hasIntercourseReminder =
          !!today && 'haveIntercourse' in today && today.haveIntercourse;
        const acknowledgements = items
          .filter((e, index) => index === tabIndex)
          .map(({ data: { id, acknowledged, updatedAt } }) => ({
            dailyInstructionId: Number(id),
            acknowledged,
            updatedAt
          }));

        return (
          <Wrapper>
            {hasIntercourseReminder && <IntercourseReminder date={today.date} />}
            <InstructionHeader>
              <p>Medications this week:</p>
            </InstructionHeader>
            <TabNavigator
              currentIndex={tabIndex}
              getSectionTitle={(index, item) => item.title}
              getUniqueKey={(index, item) => `medication-tab-nav-${item.key}`}
              items={items}
              onSelect={index => setTabIndex(index)}
              theme={{
                tab: {
                  selectedBG: themes.colors.beige,
                  deselectedBG: themes.colors.beigeLight,
                  selectedTitle: themes.colors.night,
                  deselectedTitle: themes.colors.nightLight,
                  fullWidthTabs: false
                },
                content: {
                  selectedBG: themes.colors.beige,
                  deselectedBG: themes.colors.beigeLight
                }
              }}
              renderContent={(index, item) => {
                const { drugs, instructions } = item.data;

                const count = drugs ? drugs.length : 0;
                const noDrugs = !drugs || count === 0;
                if (noDrugs && (!instructions || instructions === '')) return null;

                if (noDrugs)
                  return (
                    <InstructionNote>
                      <InstructionTitleOnly>Notes</InstructionTitleOnly>
                      <InstructionTextOnly>{instructions}</InstructionTextOnly>
                    </InstructionNote>
                  );

                return (
                  <InstructionBody>
                    {!noDrugs && <Checklist drugs={drugs} />}
                    <InstructionTitle>Notes</InstructionTitle>
                    <InstructionText>{instructions}</InstructionText>
                  </InstructionBody>
                );
              }}
            />
            <Acknowledgment acknowledgements={acknowledgements} />
          </Wrapper>
        );
      }}
    </Query>
  );
};

export default MedicationsWidget;

const Wrapper = styled.div`
  & > div > div > div {
    border-radius: 0;
  }
`;

const InstructionHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  height: 38px;
  ${({ theme }) => theme.fonts.DomaineDisplay};
  color: ${({ theme }) => theme.colors.KBNight};
  font-size: 24px;
  margin-bottom: 24px;
`;

const InstructionBody = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.KBTan};
  color: ${({ theme }) => theme.colors.KBNight};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  padding: 20px 0;

  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 20px 1fr;
  grid-gap: 9px 16px;

  & > :nth-child(1) {
    grid-column: 1;
    grid-row: 2;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
    grid-template-columns: 1.5fr 1fr;
    grid-gap: 9px 24px;
    grid-template-rows: 1fr 20px 0.2fr;

    & > :nth-child(1) {
      grid-column: 1;
      grid-column-end: span 2;
      grid-row: 1;
    }
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    grid-template-rows: 1fr 20px 0.2fr;
  }
`;

const InstructionNote = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.KBTan};
  color: ${({ theme }) => theme.colors.KBNight};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  padding: 20px 0;

  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 20px 1fr;
  grid-gap: 9px 16px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
    grid-template-columns: 1.5fr 1fr;
    grid-gap: 9px 24px;
    grid-template-rows: 1fr 20px 0.2fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    grid-template-rows: 1fr 20px 0.2fr;
  }
`;

export const InstructionTitle = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk}
  align-self: baseline;
  font-size: 14px;
  font-weight: bold;
  grid-column: 2;
  grid-row: 1;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
    grid-column: 1;
    grid-column-end: span 2;
    grid-row: 2;
  }
`;

export const InstructionText = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk}
  font-size: 14px;
  grid-column: 2;
  grid-row: 2;
  padding: 16px 22px 16px 16px;
  align-self: unset;
  background-color: ${({ theme }) => theme.colors.white};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
    grid-column: 1;
    grid-column-end: span 2;
    grid-row: 3;
  }
`;

export const InstructionTitleOnly = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk}
  align-self: baseline;
  font-size: 14px;
  font-weight: bold;
  grid-column: 1;
  grid-row: 1;
`;

export const InstructionTextOnly = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk}
  font-size: 14px;
  grid-column: 1;
  grid-row: 2;
  padding: 16px 22px 16px 16px;
  align-self: unset;
  background-color: ${({ theme }) => theme.colors.white};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
    grid-column: 1;
    grid-column-end: span 2;
    grid-row: 2;
  }
`;
